/**
 * 随机取数 · 汉字抽取
 *
 * 用途：小六壬「随机取数」模式下，当用户既未填写「所问之事」也未输入取数汉字时，
 * 由系统从常用字池中随机抽 1~10 个**不重复**汉字作为取数依据（并把抽到的字回填到「所问之事」，
 * 使历史记录与转帖能追溯本次取数所用的字）。
 *
 * 说明：
 * - 随机源可注入（`rng`），便于单测与「固定种子」复现；默认 `Math.random`。
 * - 字池在首次使用时经 cnchar 笔画库校验过滤，保证不会抽到超纲字（避免 UNKNOWN_CHAR 报错）。
 * - 本模块不属于确定性引擎：其结果会随输入一起存档，因此历史可完整复现。
 */
import cnchar from 'cnchar';
/** 候选字池原始文本（取自《千字文》开篇，皆为常用简体字） */
const POOL_RAW = '天地玄黄宇宙洪荒日月盈昃辰宿列张寒来暑往秋收冬藏闰余成岁律吕调阳云腾致雨露结为霜' +
    '金生丽水玉出昆冈剑号巨阙珠称夜光果珍李柰菜重芥姜海咸河淡鳞潜羽翔龙师火帝鸟官人皇' +
    '始制文字乃服衣裳推位让国有虞陶唐吊民伐罪周发殷汤坐朝问道垂拱平章爱育黎首臣伏戎羌' +
    '遐迩一体率宾归王鸣凤在竹白驹食场化被草木赖及万方盖此身发四大五常恭惟鞠养岂敢毁伤';
/** 已校验的字池缓存 */
let cachedPool = null;
/** 取可用字池（去重 + cnchar 笔画可识别），结果缓存 */
export function randomCharPool() {
    if (cachedPool)
        return cachedPool;
    const seen = new Set();
    const pool = [];
    for (const ch of POOL_RAW) {
        if (seen.has(ch))
            continue;
        seen.add(ch);
        const strokes = cnchar.stroke(ch, 'array');
        if ((strokes[0] ?? 0) > 0)
            pool.push(ch);
    }
    cachedPool = pool;
    return pool;
}
/**
 * 随机抽取 1~10 个不重复汉字（可直接作为笔画取数输入）。
 * min/max 会先各自收紧到 [1,10] 再取区间，越界不会抛错。
 * @example randomStrokeText() // '寒暑'
 */
export function randomStrokeText(opts = {}) {
    const { min = 1, max = 10, rng = Math.random } = opts;
    const pool = randomCharPool();
    if (pool.length === 0)
        throw new Error('随机字池为空');
    /** 收紧到 [1,10] 的整数（与笔画取数上限一致） */
    const clamp = (v) => Math.min(10, Math.max(1, Math.floor(Number.isFinite(v) ? v : 1)));
    const a = clamp(min);
    const b = clamp(max);
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const n = Math.min(pool.length, lo + Math.floor(rng() * (hi - lo + 1)));
    // Fisher–Yates 部分洗牌：取前 n 个即为不重复随机字
    const arr = [...pool];
    for (let i = 0; i < n; i += 1) {
        const j = i + Math.floor(rng() * (arr.length - i));
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    }
    return arr.slice(0, n).join('');
}
//# sourceMappingURL=random.js.map